import * as numbers from "numbers";
import * as THREE from "three/build/three.min.js";
import { TrackballControls } from "three/examples/jsm/controls/TrackballControls";

let beforeParser, parsered, result;

const clock = new THREE.Clock();
let SCREEN_WIDTH;
let SCREEN_HEIGHT;
let camera, scene, renderer, controls, materials;
let renderPaused = false;
const ATOMSCALE = 4;
const ARADIUSSCALE = [5, 3];
const BONDRADIUS = 1;

const Atom_JSON = require("./atom");
let Molecule_KNOWLEDGE = {
  colors: {},
  cdsRadius: {},
  realRadius: {},
};
for (let atom of Atom_JSON) {
  Molecule_KNOWLEDGE.colors[`${atom.symbol}`] = atom.rgb;
  Molecule_KNOWLEDGE.cdsRadius[`${atom.symbol}`] = atom.cdsRadius;
  Molecule_KNOWLEDGE.realRadius[`${atom.symbol}`] = atom.realRadius;
}

let MolecularModel = {
  Atoms: [],
  parser: {},
  midAtoms: [],
  suggestion: "",
  init(element, width, height) {
    SCREEN_WIDTH = width || 430;
    SCREEN_HEIGHT = height || 430;
    camera = new THREE.PerspectiveCamera(
      45,
      SCREEN_WIDTH / SCREEN_HEIGHT,
      10,
      5000
    );
    camera.position.z = 100 * ATOMSCALE;
    renderer = new THREE.WebGLRenderer({
      alpha: true,
      precision: "lowp",
      antialias: true,
    });
    renderer.setSize(SCREEN_WIDTH, SCREEN_HEIGHT);
    renderer.setClearColor(0xffffff, 1);
    renderer.domElement.classList.add("mole-canvas");
    renderer.domElement.style.background = "#ffffff";
    renderer.gammaInput = true;
    renderer.gammaOutput = true;
    renderer.physicallyBasedShading = true;
    renderer.shadowMap = true;
    let molecule = document.getElementById(element);
    molecule.appendChild(renderer.domElement);
    this.resize(SCREEN_WIDTH, SCREEN_HEIGHT);
    this.prepareMaterials();
  },
  dispose() {
    scene.dispose();
    renderer.dispose();
  },
  resize(width, height) {
    SCREEN_WIDTH = width || window.innerWidth;
    SCREEN_HEIGHT = height || window.innerHeight;
    camera.aspect = SCREEN_WIDTH / SCREEN_HEIGHT;
    camera.updateProjectionMatrix();
    renderer.setSize(SCREEN_WIDTH, SCREEN_HEIGHT);
  },
  createMolecule(molecule) {
    let structure = molecule;

    let parser =
      structure.type === "CIF"
        ? MolecularModel.CIFparser
        : MolecularModel.SDFparser;

    if (structure.data) {
      beforeParser = new Date();

      let struc = parser(structure.data);
      parsered = new Date();
      let parserTime = (parsered - beforeParser) / 1000;
      this.showupInterface(struc);
      result = new Date();
      let rendingTime = (result - parsered) / 1000;
      let allTime = (result - beforeParser) / 1000;
    }
    let atoms = parser(structure.data).atoms;
    this.midAtoms = [];
    for (let i = 0; i < atoms.length; i++) {
      if (this.midAtoms.indexOf(atoms[i].label) === -1) {
        this.midAtoms.push(atoms[i].label);
      }
    }
    this.createColorAtom();
    window.onresize = function () {
      MolecularModel.resize(SCREEN_WIDTH, SCREEN_HEIGHT);
    };
  },
  createColorAtom() {
    this.Atoms = [];
    for (let label of this.midAtoms) {
      let currentColor = Molecule_KNOWLEDGE.colors[label];
      let ArrayColor = currentColor
        .substr(1, currentColor.length - 2)
        .split(",");
      let rgb = `rgb(${ArrayColor[0]},${ArrayColor[1]},${ArrayColor[2]})`;
      this.Atoms.push({ label: label, rgb: rgb });
    }
  },
  render() {
    renderer.autoClear = true;
    renderer.autoClearDepth = true;
    renderer.autoClearColo = true;
    renderer.depth = true;
    renderer.stencil = false;
    renderer.autoUpdateObjects = true;
    renderer.render(scene, camera);
  },
  showupInterface(structure) {
    scene = new THREE.Scene();
    this.setupObjects(structure, 1, true);
    controls = new TrackballControls(camera, renderer.domElement);
    controls.rotateSpeed = 4;
    this.animate();
  },
  setupObjects(structure, style, doreset) {
    if (doreset === void 0) {
      doreset = false;
    }
    // Clear objects
    for (let i = scene.children.length - 1; i >= 0; i--) {
      let obj = scene.children[i];
      scene.remove(obj);
    }
    // Lights
    let Gyroscope = new THREE.Group();
    scene.add(Gyroscope);
    let ambient = new THREE.AmbientLight(0x404040);
    ambient.color.setHSL(0, 0, 0.35);
    scene.add(ambient);
    let dirLight = new THREE.DirectionalLight(0xffffff, 1);
    dirLight.position.set(1, 1, 1);
    dirLight.shadow.camera.near = 10;
    dirLight.shadow.camera.far = camera.far * 3;
    dirLight.shadow.mapSize.width = 2048;
    dirLight.shadow.mapSize.height = 2048;
    Gyroscope.add(dirLight);
    Gyroscope.add(dirLight.target);
    let sphereGeo = new THREE.SphereGeometry(1, 50, 50);
    this.addStructurePrimitives(structure, style, scene, sphereGeo, doreset);
  },
  addStructurePrimitives(structure, style, scene, sphereGeo, doreset) {
    let primitives = structure;
    for (let _i = 0, _a = primitives.atoms; _i < _a.length; _i++) {
      let atom = _a[_i];
      this.addAtom(atom, scene, sphereGeo, style);
    }
    if (style)
      for (let _b = 0, _c = primitives.bonds; _b < _c.length; _b++) {
        let bond = _c[_b];
        this.addBond(bond, scene);
      }
    for (let _d = 0, _e = primitives.lines; _d < _e.length; _d++) {
      let line = _e[_d];
      this.addLine(line, scene);
    }
    if (doreset) {
      let zmax = primitives.atoms.reduce(function (zm, a) {
        let scam = 4;
        if (a.z * scam * ATOMSCALE > zm) zm = a.z * scam * ATOMSCALE;
        if (a.x * scam * ATOMSCALE > zm) zm = a.x * scam * ATOMSCALE;
        if (a.y * scam * ATOMSCALE > zm) zm = a.y * scam * ATOMSCALE;
        return zm;
      }, 10 * ATOMSCALE);
      camera.position.z = zmax * 1.2;
    }
    this.suggestion = "数据正常";
  },
  addLine(line, scene) {
    let linegeo = new THREE.Geometry();
    let line1 = new THREE.Vector3(
      line.from.x * ATOMSCALE,
      line.from.y * ATOMSCALE,
      line.from.z * ATOMSCALE
    );
    let line2 = new THREE.Vector3(
      line.to.x * ATOMSCALE,
      line.to.y * ATOMSCALE,
      line.to.z * ATOMSCALE
    );
    linegeo.vertices.push(line1);
    linegeo.vertices.push(line2);
    linegeo.dispose();
    materials.line.dispose();
    let newline = new THREE.Line(linegeo, materials.line);
    newline.material.dispose();
    scene.add(newline);
  },
  addBond(b, scene) {
    let pt1 = new THREE.Vector3(
      b.a1.x * ATOMSCALE,
      b.a1.y * ATOMSCALE,
      b.a1.z * ATOMSCALE
    );
    let pt2 = new THREE.Vector3(
      b.a2.x * ATOMSCALE,
      b.a2.y * ATOMSCALE,
      b.a2.z * ATOMSCALE
    );
    let mid = pt1.clone().lerp(pt2, 0.5);
    let bond1 = this.cylinderMesh(
      pt1,
      mid,
      BONDRADIUS,
      materials.lambert[b.a1.label] || materials.basic
    );
    let bond2 = this.cylinderMesh(
      mid,
      pt2,
      BONDRADIUS,
      materials.lambert[b.a2.label] || materials.basic
    );
    bond1.material.dispose();
    bond2.material.dispose();
    scene.add(bond1);
    scene.add(bond2);

    materials.lambert[b.a1.label].dispose();
    materials.lambert[b.a2.label].dispose();
  },
  addAtom(atom, scene, sphereGeo, style) {
    let mat = materials.lambert[atom.label] || materials.basic;
    let radius = Molecule_KNOWLEDGE.realRadius[atom.label] || 1;
    radius = (radius - 1) * 0.5 + 1;
    let mesh = new THREE.Mesh(sphereGeo, mat);
    sphereGeo.dispose();
    mat.dispose();

    mesh.matrixAutoUpdate = false;
    mesh.position.x = atom.x * ATOMSCALE;
    mesh.position.y = atom.y * ATOMSCALE;
    mesh.position.z = atom.z * ATOMSCALE;
    mesh.scale.x = mesh.scale.y = mesh.scale.z = radius * ARADIUSSCALE[style];
    mesh.updateMatrix();
    mesh.castShadow = mesh.receiveShadow = true;
    scene.add(mesh);
    mesh.material.dispose();
  },
  cylinderMesh(vstart, vend, r, material) {
    let HALF_PI = Math.PI * 0.5;
    let distance = vstart.distanceTo(vend);
    let position = vend.clone().add(vstart).divideScalar(2);
    let cylinder = new THREE.CylinderGeometry(r, r, distance, 20, 5, true);
    let orientation = new THREE.Matrix4();
    let offsetRotation = new THREE.Matrix4();
    orientation.lookAt(vstart, vend, new THREE.Vector3(0, 1, 0));
    offsetRotation.makeRotationX(HALF_PI);
    orientation.multiply(offsetRotation);
    cylinder.applyMatrix(orientation);

    let mesh = new THREE.Mesh(cylinder, material);
    cylinder.dispose();
    material.dispose();
    mesh.position.copy(position);
    return mesh;
  },
  prepareMaterials() {
    let basicMaterial = new THREE.MeshLambertMaterial({
      color: 0x171717,
      vertexColors: THREE.FaceColors,
      precision: "lowp",
    });
    basicMaterial.preision = "lowp";
    basicMaterial.emissive.setHSL(0, 0, 0.5);
    materials = {
      basic: basicMaterial,
      flat: {},
      lambert: {},
    };
    basicMaterial.dispose();
    for (let element in Molecule_KNOWLEDGE.colors) {
      let currentColor = Molecule_KNOWLEDGE.colors[element];
      let ArrayColor = currentColor
        .substr(1, currentColor.length - 2)
        .split(",");
      let colorHex =
        (ArrayColor[0] << 16) | (ArrayColor[1] << 8) | ArrayColor[2];
      let matFlat = new THREE.MeshBasicMaterial({
        color: colorHex,
      });
      let matLambert = new THREE.MeshLambertMaterial({
        color: colorHex,
      });
      materials.flat[element] = matFlat;
      materials.lambert[element] = matLambert;
      matFlat.dispose();
      matLambert.dispose();
    }
    materials.line = new THREE.LineBasicMaterial({
      color: 0x000000,
      precision: "lowp",
    });
  },
  animate() {
    requestAnimationFrame(MolecularModel.animate);
    controls.update(clock.getDelta());
    if (!renderPaused) MolecularModel.render();
  },
  removeCIFBonds(str) {
    return str
      .replace(/^loop_\n {2}_geom.*\n( {2}.*\n)+/gm, "")
      .replace(/^_shelx_hkl_file.*\n;\n([^;\r\n].*\n)+;\n+/gm, function (m) {
        return "";
      });
  },
  CIFparser(str) {
    let mole = MolecularModel.removeCIFBonds(str);
    let _cell = ChemDoodle.readCIF(mole);
    let molecule = _cell.molecule;
    let cell = _cell.unitCell;
    let bonds = [];
    let avgx = 0;
    let avgy = 0;
    let avgz = 0;
    for (let _i = 0, _a = molecule.atoms; _i < _a.length; _i++) {
      let atom = _a[_i];
      avgx += atom.x;
      avgy += atom.y;
      avgz += atom.z;
    }
    avgx /= molecule.atoms.length;
    avgy /= molecule.atoms.length;
    avgz /= molecule.atoms.length;
    // Align the first atom to cell.o

    let atoms = MolecularModel.regularizeCell(
      molecule.atoms,
      cell.unitCell,
      function (x, y, z, a) {
        return {
          x: x,
          y: y,
          z: z,
          label: a.label,
        };
      }
    );

    for (let j = 0; j < atoms.length; j++)
      for (let k = 0; k < j; k++) {
        let a1 = atoms[j];
        let a2 = atoms[k];
        let distance = Math.sqrt(
          (a1.x - a2.x) * (a1.x - a2.x) +
            (a1.y - a2.y) * (a1.y - a2.y) +
            (a1.z - a2.z) * (a1.z - a2.z)
        );
        let sqdistance =
          (a1.x - a2.x) * (a1.x - a2.x) +
          (a1.y - a2.y) * (a1.y - a2.y) +
          (a1.z - a2.z) * (a1.z - a2.z);
        let radsum =
          Molecule_KNOWLEDGE.cdsRadius[a1.label] -
          0 +
          (Molecule_KNOWLEDGE.cdsRadius[a2.label] - 0);

        let uplimit = 1.1 * radsum * 1.1 * radsum;
        let downlimit = 0.5 * radsum * 0.5 * radsum;
        if (sqdistance <= uplimit) {
          bonds.push({
            a1: atoms[j],
            a2: atoms[k],
            bondOrder: 1,
          });
        }
      }
    let cellatoms = {};
    let phantoms = Object.keys(cell.unitCell).map(function (k) {
      let v = cell.unitCell[k];
      let a = new THREE.Vector3(v[0], v[1], v[2]);
      cellatoms[k] = a;
      return a;
    });

    return {
      id: "Structure",
      type: "Crystal",
      atoms: atoms,
      bonds: bonds,
      lines: [
        { from: cellatoms.o, to: cellatoms.x },
        { from: cellatoms.o, to: cellatoms.y },
        { from: cellatoms.o, to: cellatoms.z },
        { from: cellatoms.x, to: cellatoms.xy },
        { from: cellatoms.y, to: cellatoms.yz },
        { from: cellatoms.z, to: cellatoms.xz },
        { from: cellatoms.y, to: cellatoms.xy },
        { from: cellatoms.z, to: cellatoms.yz },
        { from: cellatoms.x, to: cellatoms.xz },
        { from: cellatoms.xy, to: cellatoms.xyz },
        { from: cellatoms.yz, to: cellatoms.xyz },
        { from: cellatoms.xz, to: cellatoms.xyz },
      ],
    };
  },
  SDFparser(str) {
    let molecule = ChemDoodle.readMOL(str, 1);
    return {
      id: "Structure",
      type: "SDF",
      atoms: molecule.atoms,
      bonds: molecule.bonds,
      lines: [],
    };
  },
  regularizeCell(atoms, cell, make) {
    let shifts = [
      [0, 0, 0],
      [1, 0, 0],
      [0, 1, 0],
      [0, 0, 1],
      [1, 1, 0],
      [0, 1, 1],
      [1, 0, 1],
      [1, 1, 1],
    ];
    let delta = 1e-2;
    let a = [
      cell.x[0] - cell.o[0],
      cell.x[1] - cell.o[1],
      cell.x[2] - cell.o[2],
    ];
    let b = [
      cell.y[0] - cell.o[0],
      cell.y[1] - cell.o[1],
      cell.y[2] - cell.o[2],
    ];
    let c = [
      cell.z[0] - cell.o[0],
      cell.z[1] - cell.o[1],
      cell.z[2] - cell.o[2],
    ];
    let A = numbers.matrix.transpose([a, b, c]);
    let invA = numbers.matrix.inverse(numbers.matrix.deepCopy(A));
    let ans = [];
    // Find out atom with smallest fractional coordinates
    let ab = numbers.matrix.dotproduct(a, b);
    let bc = numbers.matrix.dotproduct(b, c);
    let ac = numbers.matrix.dotproduct(a, c);
    let axisswap = [0, 1, 2];
    let coeff = [1, -1, -1];
    if (Math.abs(ab) < delta && Math.abs(bc) < delta && Math.abs(ac) >= delta) {
      axisswap = [0, 2, 1];
      coeff = [-1, -1, -1];
    } else if (
      Math.abs(ab) < delta &&
      Math.abs(ac) < delta &&
      Math.abs(bc) >= delta
    ) {
      axisswap = [1, 0, 2];
      coeff = [-1, -1, -1];
    }
    for (let _i = 0, shifts_1 = shifts; _i < shifts_1.length; _i++) {
      let shift = shifts_1[_i];
      for (let _a = 0, atoms_1 = atoms; _a < atoms_1.length; _a++) {
        let atom = atoms_1[_a];
        let fractional = numbers.matrix.multiply(invA, [
          [atom.x],
          [atom.y],
          [atom.z],
        ]);
        let u = fractional[0][0],
          v = fractional[1][0],
          w = fractional[2][0];
        (u -= Math.floor(u)), (v -= Math.floor(v)), (w -= Math.floor(w));
        if (u >= 1 - delta) u -= 1;
        if (v >= 1 - delta) v -= 1;
        if (w >= 1 - delta) w -= 1;
        (u += shift[0]), (v += shift[1]), (w += shift[2]);
        if (
          u >= -delta &&
          u <= 1 + delta &&
          v >= -delta &&
          v <= 1 + delta &&
          w >= -delta &&
          w <= 1 + delta
        ) {
          let cartesian = numbers.matrix.multiply(A, [[u], [v], [w]]);
          let rpos = [
            cell.o[0] + cartesian[0][0],
            cell.o[1] + cartesian[1][0],
            cell.o[2] + cartesian[2][0],
          ];
          ans.push(
            make(
              coeff[0] * rpos[axisswap[0]],
              coeff[1] * rpos[axisswap[1]],
              coeff[2] * rpos[axisswap[2]],
              atom
            )
          );
        }
      }
    }

    function applyswapcoeff(v, s, c) {
      let v1 = [v[0], v[1], v[2]];
      v[0] = c[0] * v1[s[0]];
      v[1] = c[1] * v1[s[1]];
      v[2] = c[2] * v1[s[2]];
    }

    applyswapcoeff(cell.o, axisswap, coeff);
    applyswapcoeff(cell.x, axisswap, coeff);
    applyswapcoeff(cell.y, axisswap, coeff);
    applyswapcoeff(cell.z, axisswap, coeff);
    applyswapcoeff(cell.xy, axisswap, coeff);
    applyswapcoeff(cell.yz, axisswap, coeff);
    applyswapcoeff(cell.xz, axisswap, coeff);
    applyswapcoeff(cell.xyz, axisswap, coeff);
    return ans;
  },
};

/* helpers */
export default MolecularModel;
